import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Me, type PublicConfig } from '../lib/api';

/**
 * Profile page: view and edit first/last name. Email is read-only. Shows
 * enrollment start date, current program week, and the support email students
 * can write to with questions.
 */
export default function ProfilePage() {
  const nav = useNavigate();
  const [me, setMe] = useState<Me['user']>(null);
  const [supportEmail, setSupportEmail] = useState<string>('marshall@constare.academy');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [meRes, cfgRes] = await Promise.all([
          api.get<Me>('/api/auth/me'),
          api.get<PublicConfig>('/api/config/public').catch(() => null),
        ]);
        setMe(meRes.user);
        setFirstName(meRes.user?.firstName ?? '');
        setLastName(meRes.user?.lastName ?? '');
        if (cfgRes?.supportEmail) setSupportEmail(cfgRes.supportEmail);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setStatus('idle');
    try {
      const res = await api.patch<{ user: Me['user'] }>('/api/auth/profile', {
        firstName: firstName || null,
        lastName: lastName || null,
      });
      setMe(res.user);
      setStatus('saved');
      // Clear the "Saved" pill after a moment.
      setTimeout(() => setStatus('idle'), 2500);
    } catch {
      setStatus('error');
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      nav('/');
    }
  }

  if (loading) {
    return <div className="py-6 text-muted">Loading…</div>;
  }
  if (!me) {
    // Not signed in, bounce to login.
    nav('/login');
    return null;
  }

  const enrolledAt = me.enrolledAt ? new Date(me.enrolledAt) : null;
  const enrolledLabel = enrolledAt
    ? enrolledAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Not yet enrolled';

  // Week the student is currently on, based on enrollment date. Week 1 is the
  // first 7 days from enrolledAt.
  const currentWeek = (() => {
    if (!enrolledAt) return null;
    const daysSince = Math.floor((Date.now() - enrolledAt.getTime()) / (1000 * 60 * 60 * 24));
    const week = Math.min(8, Math.floor(daysSince / 7) + 1);
    return week > 0 ? week : 1;
  })();

  return (
    <div className="py-6 max-w-2xl">
      <h1 className="text-3xl font-semibold tracking-tight">Profile</h1>
      <p className="mt-2 text-muted">
        Your account details. Update your name so we can address you properly in emails.
      </p>

      {/* Program status card */}
      <div className="mt-8 rounded-xl border border-line bg-surface p-5">
        <div className="text-xs uppercase tracking-widest text-amber font-mono">Program status</div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-xs text-muted">Enrolled</div>
            <div className="mt-1 text-sm">{enrolledLabel}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Current week</div>
            <div className="mt-1 text-sm">
              {currentWeek ? `Week ${currentWeek} of 8` : 'Not yet enrolled'}
            </div>
          </div>
        </div>
      </div>

      {/* Name form */}
      <form onSubmit={handleSave} className="mt-8 rounded-xl border border-line bg-surface p-5">
        <div className="text-xs uppercase tracking-widest text-amber font-mono">Your name</div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="block text-xs text-muted mb-1">First name</span>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={80}
              className="w-full rounded-md border border-line bg-ink px-3 py-2 text-sm outline-none focus:border-amber"
              placeholder="Optional"
              data-testid="input-first-name"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-muted mb-1">Last name</span>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={80}
              className="w-full rounded-md border border-line bg-ink px-3 py-2 text-sm outline-none focus:border-amber"
              placeholder="Optional"
              data-testid="input-last-name"
            />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="rounded-md border border-amber bg-amber/10 px-4 py-2 text-sm text-amber hover:bg-amber/20 disabled:opacity-50"
            data-testid="button-save-profile"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {status === 'saved' && <span className="text-xs text-muted">Saved</span>}
          {status === 'error' && (
            <span className="text-xs text-red-400">Save failed. Try again.</span>
          )}
        </div>
      </form>

      {/* Account details */}
      <div className="mt-8 rounded-xl border border-line bg-surface p-5">
        <div className="text-xs uppercase tracking-widest text-amber font-mono">Account</div>
        <div className="mt-4 grid gap-3">
          <div>
            <div className="text-xs text-muted">Email</div>
            <div className="mt-1 text-sm" data-testid="text-email">
              {me.email}
            </div>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="mt-8 rounded-xl border border-line bg-surface p-5">
        <div className="text-xs uppercase tracking-widest text-amber font-mono">Questions?</div>
        <p className="mt-3 text-sm text-muted">
          Have a question about the program? Email us at{' '}
          <a
            href={`mailto:${supportEmail}`}
            className="text-amber hover:underline"
            data-testid="link-support-email"
          >
            {supportEmail}
          </a>
          . A real person reads that inbox.
        </p>
        <p className="mt-2 text-xs text-muted">
          For lesson feedback, use the Feedback button on any lesson page.
        </p>
      </div>

      {/* Sign out */}
      <div className="mt-8">
        <button
          onClick={handleLogout}
          className="text-sm text-muted hover:text-text underline"
          data-testid="button-sign-out"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
